import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import './CareDashboard.css';

/**
 * Normalize a user-typed phone number to E.164, rejecting non-numeric input.
 * Without this, a name typed in the phone field (e.g. "Williams") is sent
 * straight to Twilio, whose vanity-number handling converts the letters on a
 * keypad to digits and silently dials a bogus number (Williams → +94554267),
 * which then fails with a confusing geo-permissions error.
 */
function normalizePhone(raw) {
  const trimmed = (raw || '').trim();
  if (!trimmed) return { ok: false, msg: 'Please enter a phone number.' };
  if (/[a-zA-Z]/.test(trimmed)) {
    return { ok: false, msg: 'Phone number can’t contain letters — enter digits, e.g. +1 555 555 0123.' };
  }
  const hasPlus = trimmed.startsWith('+');
  const digits = trimmed.replace(/\D/g, '');
  if (hasPlus) {
    if (digits.length < 8 || digits.length > 15) return { ok: false, msg: 'That doesn’t look like a valid phone number.' };
    return { ok: true, value: '+' + digits };
  }
  if (digits.length === 10) return { ok: true, value: '+1' + digits };                       // US/Canada, no country code
  if (digits.length === 11 && digits.startsWith('1')) return { ok: true, value: '+' + digits };
  return { ok: false, msg: 'Add a country code, e.g. +1 for US numbers: +1 555 555 0123.' };
}

/**
 * Add / Edit Managed Account modal.
 *
 * Create mode (account prop absent): posts to POST /api/managed-accounts and
 * calls onCreated with the new account object.
 *
 * Edit mode (account prop present): pre-fills form fields; PATCHes
 * /api/managed-accounts/{id} on submit; calls onSaved after success.
 * The relationship field is hidden in edit mode (it lives on the caregiver
 * binding, not the profile). A primary-only "Remove this person" action
 * appears at the bottom when account.role === 'primary'.
 *
 * Styled on the dark Care-dashboard theme. Reuses .care-field / .care-input /
 * .care-btn classes from CareDashboard.css.
 */
export default function AddManagedAccountModal({ token, api, onClose, onCreated, onSaved, account }) {
  const isEdit = Boolean(account);

  const [name, setName] = useState(isEdit ? (account.name || '') : '');
  const [phone, setPhone] = useState(isEdit ? (account.phone_number || '') : '');
  const [relationship, setRelationship] = useState('');
  const [condition, setCondition] = useState(isEdit ? (account.condition_label || 'dementia') : 'dementia');
  const [companionName, setCompanionName] = useState(isEdit ? (account.companion_name || 'Maple') : 'Maple');
  const [notifyPhone, setNotifyPhone] = useState('');
  const [attested, setAttested] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Remove flow state
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false);
  const [removing, setRemoving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (isEdit) {
      if (!name.trim()) return;
    } else {
      if (!name.trim() || !phone.trim() || !relationship.trim() || !attested) return;
    }
    // Validate/normalize the phone number — both create and edit set it, and a
    // bad value here is what silently breaks outbound calls.
    const ph = normalizePhone(phone);
    if (!ph.ok) { setError(ph.msg); return; }
    setSubmitting(true);
    setError('');
    try {
      if (isEdit) {
        const res = await fetch(`${api}/api/managed-accounts/${account.id}`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: name.trim(),
            phone_number: ph.value,
            condition_label: condition.trim() || 'dementia',
            companion_name: companionName.trim() || 'Maple',
          }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${res.status}`);
        }
        onSaved?.();
      } else {
        const res = await fetch(`${api}/api/managed-accounts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            name: name.trim(),
            phone_number: ph.value,
            relationship: relationship.trim(),
            condition_label: condition.trim() || 'dementia',
            companion_name: companionName.trim() || 'Maple',
            notify_phone: notifyPhone.trim() || null,
          }),
        });
        if (!res.ok) {
          const d = await res.json().catch(() => ({}));
          throw new Error(d.detail || `HTTP ${res.status}`);
        }
        const created = await res.json();
        onCreated?.(created);
      }
    } catch (err) {
      setError(err.message || (isEdit ? 'Failed to save changes.' : 'Failed to create account.'));
      setSubmitting(false);
    }
  };

  const handleRemove = async () => {
    setRemoving(true);
    setError('');
    try {
      const res = await fetch(`${api}/api/managed-accounts/${account.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setError('Only the primary caregiver can remove this account.');
        setRemoving(false);
        setShowRemoveConfirm(false);
        return;
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || `HTTP ${res.status}`);
      }
      onSaved?.({ removed: true, id: account.id });
    } catch (err) {
      setError(err.message || 'Failed to remove account.');
      setRemoving(false);
      setShowRemoveConfirm(false);
    }
  };

  const field = (label, input) => (
    <label className="care-field">
      <div className="care-field-label">{label}</div>
      {input}
    </label>
  );

  return ReactDOM.createPortal(
    <div className="care-modal-overlay" onClick={onClose}>
      <div className="care-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={isEdit ? 'Edit managed account' : 'Add managed account'}>
        <div className="care-modal-header">
          <h2>{isEdit ? 'Edit account' : 'Add managed account'}</h2>
          <button className="care-dash-close" onClick={onClose} aria-label="Close">×</button>
        </div>
        <form className="care-modal-body" onSubmit={submit}>
          {!isEdit && (
            <p className="care-modal-help">
              Set up a companion for a family member or loved one. You&apos;ll be able
              to schedule reminders and see what they share over the phone.
            </p>
          )}

          {field('Their name', (
            <input className="care-input" type="text" autoFocus placeholder="e.g. Margaret"
              value={name} onChange={(e) => setName(e.target.value)} />
          ))}
          {field("Phone number (used for caller-ID; they won’t need a PIN)", (
            <input className="care-input" type="tel" placeholder="+15555550100"
              value={phone} onChange={(e) => setPhone(e.target.value)} />
          ))}
          {!isEdit && field('Your relationship to them', (
            <input className="care-input" type="text" placeholder="mother, husband, friend, etc."
              value={relationship} onChange={(e) => setRelationship(e.target.value)} />
          ))}
          {field('Condition', (
            <input className="care-input" type="text"
              value={condition} onChange={(e) => setCondition(e.target.value)} />
          ))}
          {field('What should the companion call itself?', (
            <input className="care-input" type="text"
              value={companionName} onChange={(e) => setCompanionName(e.target.value)} />
          ))}
          {!isEdit && field('Your mobile (for crisis alerts) — optional', (
            <input className="care-input" type="tel" placeholder="+1 555 555 0123"
              value={notifyPhone} onChange={(e) => setNotifyPhone(e.target.value)} />
          ))}

          {!isEdit && (
            <label className="care-modal-consent">
              <input type="checkbox" checked={attested} onChange={(e) => setAttested(e.target.checked)} />
              <span>
                I confirm I have authority to set up this companion account on
                their behalf, and that they would consent to me doing so.
              </span>
            </label>
          )}

          {error && <div className="care-error-state" style={{ marginBottom: 12 }} role="alert">{error}</div>}

          <div className="care-modal-footer">
            <button type="button" className="care-btn" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="care-btn primary"
              disabled={submitting || !name.trim() || (!isEdit && (!phone.trim() || !relationship.trim() || !attested))}
            >
              {submitting ? (isEdit ? 'Saving…' : 'Creating…') : (isEdit ? 'Save changes' : 'Create account')}
            </button>
          </div>

          {isEdit && account.role === 'primary' && (
            <div className="care-remove-section">
              {!showRemoveConfirm ? (
                <button
                  type="button"
                  className="care-btn care-btn-danger"
                  onClick={() => setShowRemoveConfirm(true)}
                >
                  Remove this person
                </button>
              ) : (
                <div className="care-remove-confirm">
                  <p className="care-remove-confirm-text">
                    Remove <strong>{account.name}</strong>? Their memories are kept; this can be undone by support.
                  </p>
                  <div className="care-remove-confirm-actions">
                    <button type="button" className="care-btn" onClick={() => setShowRemoveConfirm(false)} disabled={removing}>Cancel</button>
                    <button type="button" className="care-btn care-btn-danger" onClick={handleRemove} disabled={removing}>
                      {removing ? 'Removing…' : 'Confirm remove'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    </div>,
    document.body,
  );
}
